import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { format, parseISO } from "date-fns";
import {
  ArrowLeft,
  AlertTriangle,
  MapPin,
  Phone,
  Clock,
  ClipboardList,
  User,
  Sparkles,
  Receipt,
  ChevronRight,
  Hammer,
} from "lucide-react";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { TechAvatar } from "@/components/TechAvatar";
import { CustomerLink, TechLink } from "@/components/links";
import { CustomerAvatar } from "@/features/customer-360/CustomerAvatar";
import { CustomerProseSummary } from "@/features/customer-360/CustomerProseSummary";
import {
  displayCustomerName,
  formatTime,
  formatDuration,
  resolveWindowAndPriority,
} from "@/lib/format";
import { StatusBadge } from "@/components/ui/StatusBadge";
import { WindowBadge } from "@/components/ui/WindowBadge";
import { TestRecapStrip } from "./TestRecapStrip";
import { useCustomer } from "@/features/customer-360/api";
import { useRealtimeRefresh } from "@/lib/useRealtimeRefresh";
import { usePermissions } from "@/lib/permissions/PermissionProvider";
import { useDispatch, groupTechAssignments, type TechGroup } from "./api";
import { useSalesByDispatch } from "@/features/invoice-detail/api";
import { useBackTarget, backText } from "@/lib/navBack";
import { SellInstallModal } from "@/features/jobs/JobsView";
import { DispatchFiles } from "@/features/files/DispatchFiles";
import { CopyCode } from "@/components/Copyable";

// ============================================================================
// DispatchDetailView — `/dispatch/:dispatch`
//
// Two-column layout:
//   Left  — the dispatch itself (date, window, status, notes, assigned tech(s),
//           estimate, address, customer phone)
//   Right — customer context mini-panel (avatar + prose summary + click-through
//           to full Customer 360)
//
// Smart back: reads ?from=... param to decide where "← Back" returns to.
//   from=board → /board with the dispatch's date + tech preserved
//   from=search → /customers?q=... preserved
//   no from → /board (today)
//
// Realtime: subscribes to mirror_dispatch + mirror_disptech so the dispatcher
// sees status changes from the field (tech marks Working/Complete) without a
// manual refresh.
// ============================================================================

export function DispatchDetailView() {
  const { dispatch: dispatchId } = useParams<{ dispatch: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { data: dispatch, isLoading, isError, error } = useDispatch(dispatchId);
  const { isAdmin } = usePermissions();
  const { data: customer } = useCustomer(dispatch?.custno);
  const { data: sales } = useSalesByDispatch(dispatchId);
  const [selling, setSelling] = useState(false);
  // One entry per TECH (multi-day installs carry one row per tech per day).
  const techGroups = dispatch ? groupTechAssignments(dispatch.techs) : [];

  // Realtime — bump the cache when either underlying mirror table changes.
  useRealtimeRefresh(
    ["mirror_dispatch", "mirror_disptech"],
    [["dispatch_detail", dispatchId ?? ""]],
  );

  // Browser tab title
  useEffect(() => {
    if (dispatch) {
      const cust = displayCustomerName(
        dispatch.locname,
        dispatch.customer_fullname,
      );
      document.title = `Pegasus · Dispatch ${dispatch.dispatch} · ${cust}`;
    } else {
      document.title = "Pegasus · Dispatch";
    }
  }, [dispatch]);

  // --- Back navigation -----------------------------------------------------
  // Prefer the screen the smart-link recorded (NAV-03 — works from anywhere).
  // Fall back to the legacy ?from= logic for callers that still set it, or a
  // board default on hard refresh / direct entry.
  const from = searchParams.get("from");
  const fallbackLabel =
    from === "search" ? "search" : from === "customer" ? "customer" : "board";
  const fallbackUrl = (() => {
    if (from === "search") {
      const q = searchParams.get("q");
      return q ? `/customers?q=${encodeURIComponent(q)}` : "/customers";
    }
    if (from === "customer" && dispatch) {
      return `/customers/${dispatch.custno}`;
    }
    // Default: back to board, with the dispatch's date + first tech preserved
    if (dispatch) {
      const params = new URLSearchParams();
      params.set("date", dispatch.appointment_date);
      const firstTech = dispatch.techs[0]?.tech_empno;
      if (firstTech) params.set("tech", firstTech);
      return `/board?${params.toString()}`;
    }
    return "/board";
  })();
  const back = useBackTarget({ url: fallbackUrl, label: fallbackLabel });

  if (isLoading) {
    return (
      <div className="mx-auto max-w-6xl space-y-4 p-8">
        <Card className="h-12 animate-pulse bg-slate-100" />
        <div className="grid gap-4 lg:grid-cols-3">
          <Card className="h-96 animate-pulse bg-slate-100 lg:col-span-2" />
          <Card className="h-72 animate-pulse bg-slate-100" />
        </div>
      </div>
    );
  }

  if (isError || !dispatch) {
    return (
      <div className="mx-auto max-w-2xl p-8">
        <Card className="border-red-200 bg-red-50 p-5">
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-0.5 h-5 w-5 text-red-600" />
            <div>
              <h3 className="text-sm font-semibold text-red-900">
                Couldn&apos;t load dispatch {dispatchId}
              </h3>
              <p className="mt-1 text-xs text-red-700">
                {(error as Error)?.message ?? "Dispatch not found."}
              </p>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => navigate(back.url)}
                className="mt-3"
              >
                {backText(back.label)}
              </Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  const customerName = displayCustomerName(
    dispatch.locname,
    dispatch.customer_fullname,
  );
  const { timeWindow, priority } = resolveWindowAndPriority(
    dispatch.window_code,
    dispatch.notes,
  );
  const promTime = formatTime(dispatch.tpromtime);

  let dateLabel = dispatch.appointment_date;
  try {
    dateLabel = format(parseISO(dispatch.appointment_date), "EEEE, MMMM d, yyyy");
  } catch {
    /* keep raw string */
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.15 }}
      className="mx-auto max-w-6xl p-8"
    >
      {/* Smart back */}
      <button
        type="button"
        onClick={() => navigate(back.url)}
        className="mb-4 inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 transition-colors duration-150 ease-out-quart hover:text-brand-700"
      >
        <ArrowLeft className="h-3 w-3" />
        {backText(back.label)}
      </button>

      {/* Header */}
      <section className="mb-6">
        <div className="flex flex-wrap items-baseline gap-3">
          <h2 className="text-2xl font-bold text-slate-900">
            Dispatch{" "}
            <CopyCode value={dispatch.dispatch} label="Copy call number" variant="text" size="lg" className="text-2xl font-bold text-slate-700" />
          </h2>
          <span className="text-sm text-slate-500">{dateLabel}</span>
          {promTime && (
            <span className="inline-flex items-center gap-1 text-sm text-slate-500">
              <Clock className="h-3 w-3" />
              {promTime}
            </span>
          )}
          <Button size="sm" onClick={() => setSelling(true)} className="ml-auto self-center shrink-0">
            <Hammer className="h-4 w-4" /> Sell an install
          </Button>
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {timeWindow && <WindowBadge code={timeWindow} prefix="Window" />}
          {priority && <WindowBadge code={priority} prefix="Priority" />}
          {dispatch.dispatch_priority && (
            <span className="rounded-full bg-slate-50 px-2.5 py-0.5 text-xs text-slate-700">
              Type: {dispatch.dispatch_priority}
            </span>
          )}
          {dispatch.multitech === "T" && (
            <span className="rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-700">
              Multi-tech
            </span>
          )}
        </div>
      </section>

      {selling && (
        <SellInstallModal
          prefill={{
            customer: {
              custno: dispatch.custno,
              locno: dispatch.locno ?? "",
              name: customerName,
              address: [dispatch.service_address, dispatch.city].filter(Boolean).join(", ") || null,
            },
            sourceDispatch: dispatch.dispatch,
          }}
          onClose={() => setSelling(false)}
          onCreated={() => { setSelling(false); navigate("/jobs"); }}
        />
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Left — dispatch details (2/3 width on large screens) */}
        <div className="space-y-4 lg:col-span-2">
          {/* Notes */}
          <Card className="p-5">
            <SectionHeader icon={ClipboardList} label="Dispatch notes" />
            {dispatch.notes ? (
              <p className="whitespace-pre-wrap rounded-md bg-slate-50 p-3 font-mono text-xs leading-relaxed text-slate-700">
                {dispatch.notes}
              </p>
            ) : (
              <p className="text-sm italic text-slate-500">
                No notes on this dispatch.
              </p>
            )}
          </Card>

          {/* Assigned techs — one row per TECH, blocks consolidated (Seth
              2026-06-11: a 12-day install listed the same tech 12 times). */}
          <Card className="p-5">
            <SectionHeader
              icon={User}
              label={
                techGroups.length > 1
                  ? `${techGroups.length} assigned techs`
                  : "Assigned tech"
              }
            />
            <div className="space-y-2">
              {techGroups.map((g) => (
                <TechGroupRow key={g.tech_empno} group={g} />
              ))}
            </div>
          </Card>

          {/* Estimate */}
          {(dispatch.techs.some((t) => t.tech_est_hours) ||
            !!dispatch.dispatch_est_min) && (
            <Card className="p-5">
              <SectionHeader icon={Clock} label="Estimate" />
              <div className="text-sm text-slate-700">
                {dispatch.dispatch_est_min ? (
                  <div>
                    Dispatch-level:{" "}
                    <span className="font-medium">
                      {formatDuration(dispatch.dispatch_est_min)}
                    </span>
                  </div>
                ) : null}
                {/* Per-TECH totals — a 12-day install reads as one line per
                    tech, not twelve (Seth 2026-06-11). */}
                {techGroups
                  .filter((g) => g.totalEstHours > 0)
                  .map((g) => (
                    <div key={g.tech_empno}>
                      Per {g.name}:{" "}
                      <span className="font-medium">
                        {formatDuration(g.totalEstHours)}
                      </span>
                      {g.days.length > 1 && (
                        <span className="text-slate-500">
                          {" "}
                          across {g.days.length} days
                        </span>
                      )}
                    </div>
                  ))}
              </div>
            </Card>
          )}

          {/* Service address */}
          {(dispatch.service_address || dispatch.city) && (
            <Card className="p-5">
              <SectionHeader icon={MapPin} label="Service address" />
              <div className="text-sm text-slate-700">
                {dispatch.locname && (
                  <div className="font-medium text-slate-900">
                    {dispatch.locname}
                  </div>
                )}
                {dispatch.service_address && (
                  <div>{dispatch.service_address}</div>
                )}
                {(dispatch.city || dispatch.state || dispatch.zip) && (
                  <div>
                    {dispatch.city}
                    {dispatch.city && dispatch.state && ", "}
                    {dispatch.state} {dispatch.zip}
                  </div>
                )}
              </div>
            </Card>
          )}

          {/* Customer phone */}
          {dispatch.customer_phone && (
            <Card className="p-5">
              <SectionHeader icon={Phone} label="Customer phone" />
              <a
                href={`tel:${dispatch.customer_phone}`}
                className="text-sm text-brand-700 hover:underline"
              >
                {dispatch.customer_phone}
              </a>
            </Card>
          )}

          {/* Files pinned to this dispatch (FILES-03) — permits, install
              forms, job-site photos; most of the ESC import lives here. */}
          <Card className="p-5">
            <DispatchFiles
              dispatch={dispatch.dispatch}
              custno={dispatch.custno}
              locno={dispatch.locno}
            />
          </Card>

          {/* Invoice(s) — NAV-01 dispatch -> invoice link */}
          {sales && sales.length > 0 && (
            <Card className="p-5">
              <SectionHeader icon={Receipt} label={sales.length > 1 ? "Invoices" : "Invoice"} />
              <div className="space-y-2">
                {sales.map((s) => (
                  <button
                    key={s.invoice}
                    type="button"
                    onClick={() =>
                      navigate(`/invoice/${s.invoice}?from=dispatch&dispatch=${dispatch.dispatch}`)
                    }
                    className="group flex w-full items-center justify-between rounded-md bg-slate-50 px-3 py-2 text-left transition-colors hover:bg-brand-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30"
                  >
                    <span className="text-sm text-slate-700">
                      <span className="font-mono font-medium text-slate-900">#{s.invoice}</span>
                      {s.inv_type ? ` · ${s.inv_type}` : ""}
                    </span>
                    <span className="inline-flex items-center gap-2 text-sm">
                      <span className="font-medium text-slate-900">
                        {s.inv_amount != null
                          ? s.inv_amount.toLocaleString("en-US", { style: "currency", currency: "USD" })
                          : ""}
                      </span>
                      <ChevronRight className="h-4 w-4 text-slate-300 group-hover:text-brand-500" />
                    </span>
                  </button>
                ))}
              </div>
            </Card>
          )}
        </div>

        {/* Right — customer context (1/3 width on large screens) */}
        <div className="space-y-4">
          {/* FLOW-SIMPLER-01 (Seth 2026-10-07: the dispatch-screen checklist
              was "completely confusing"): ESC-era installs are handled
              manually, so the DISPATCH screen carries only this one-line
              admin test rig. The real close-out board lives on the Pegasus
              job's Post-Install tab. */}
          {dispatch.window_code === "INSTAL" && isAdmin && (
            <TestRecapStrip dispatch={dispatch.dispatch} custno={dispatch.custno} locno={dispatch.locno} />
          )}
          <Card className="p-5">
            <SectionHeader icon={Sparkles} label="Customer" />
            <div className="flex items-start gap-3">
              <CustomerAvatar
                custno={dispatch.custno}
                name={customerName}
                address={
                  dispatch.service_address
                    ? {
                        add1: dispatch.service_address,
                        city: dispatch.city,
                        state: dispatch.state,
                        zip: dispatch.zip,
                      }
                    : null
                }
                size="md"
              />
              <div className="min-w-0 flex-1">
                <CustomerLink
                  custno={dispatch.custno}
                  locno={dispatch.locno}
                  className="block text-sm"
                >
                  {customerName}
                </CustomerLink>
                <div className="mt-0.5 font-mono text-xs text-slate-500">
                  {dispatch.custno}
                </div>
              </div>
            </div>
            <div className="mt-3">
              <CustomerProseSummary
                custno={dispatch.custno}
                locationCount={customer?.locations?.length ?? 1}
              />
            </div>
            <div className="mt-4 border-t border-slate-100 pt-3">
              <Button
                variant="primary"
                size="sm"
                onClick={() =>
                  navigate(
                    `/customers/${dispatch.custno}?from=dispatch&dispatch=${dispatch.dispatch}`,
                  )
                }
                className="w-full"
              >
                Qualify
              </Button>
            </div>
          </Card>
        </div>
      </div>
    </motion.div>
  );
}

// ----------------------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------------------

function SectionHeader({
  icon: Icon,
  label,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
}) {
  return (
    <div className="mb-3 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
      <Icon className="h-3 w-3" />
      {label}
    </div>
  );
}

/** One row per TECH — blocks consolidated (Seth 2026-06-11). Headline:
 *  name + days on the job + date span + total est hours; the per-day
 *  schedule expands on demand instead of stacking 12 identical rows. */
function TechGroupRow({ group }: { group: TechGroup }) {
  const [expanded, setExpanded] = useState(false);
  const multiDay = group.days.length > 1;

  const fmtDay = (iso: string) => {
    try {
      return format(parseISO(iso), "MMM d, yyyy");
    } catch {
      return iso;
    }
  };
  const span = multiDay
    ? `${fmtDay(group.firstDate)} – ${fmtDay(group.lastDate)}`
    : group.firstDate
      ? fmtDay(group.firstDate)
      : null;

  return (
    <div className="rounded-md bg-slate-50 px-3 py-2">
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <TechAvatar empno={group.tech_empno} name={group.name} size="sm" />
          <div className="min-w-0">
            <TechLink empno={group.tech_empno} name={group.name} className="text-sm" />
            <div className="truncate text-xs text-slate-500">
              {multiDay
                ? `${group.days.length} days on the job · ${span}`
                : span ?? (group.dept ? `Dept ${group.dept}` : "")}
              {group.totalEstHours > 0 &&
                ` · ${formatDuration(group.totalEstHours)} est`}
            </div>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {multiDay && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="rounded-md px-1.5 py-0.5 text-[11px] font-medium text-brand-600 transition-colors hover:bg-brand-50 hover:text-brand-800"
            >
              {expanded ? "Hide days" : `Show ${group.days.length} days`}
            </button>
          )}
          <StatusBadge status={group.latestStatus} variant="pill" />
        </div>
      </div>

      {expanded && (
        <ul className="mt-2 space-y-0.5 border-t border-slate-200/70 pt-2">
          {group.blocks.map((b, i) => (
            <li
              key={`${b.counter ?? i}-${b.appointment_date}`}
              className="flex items-center justify-between gap-2 px-1 text-xs text-slate-600"
            >
              <span>{fmtDay(b.appointment_date)}</span>
              <span className="flex items-center gap-2 tabular-nums text-slate-400">
                {b.tech_est_hours ? formatDuration(b.tech_est_hours) : ""}
                <span className="w-20 text-right">{b.status ?? ""}</span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
